import React from "react";
import { ChevronRight, Hourglass, Loader2, MapPin, CloudSun } from "lucide-react";
import { Tile } from "../Primitives";
import { OriginBadge, syn } from "../../lib/origin.jsx";
import { ABCXYZ_COMPLIANCE } from "../../lib/dataExtras";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

const ROW_LABEL = { A: "Top 70% value", B: "Next 20%", C: "Last 10%" };

function Meter({ code }) {
  const comp = ABCXYZ_COMPLIANCE[code];
  if (!comp) return null;
  const tone = comp.status === "success" ? "text-success" : comp.status === "warning" ? "text-warning" : "text-critical";
  const bg = comp.status === "success" ? "bg-success" : comp.status === "warning" ? "bg-warning" : "bg-critical";
  return (
    <div data-testid={`abcxyz-comp-${code}`} className="mt-1.5 pt-1.5 border-t border-border/60">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest mb-0.5">
        <span className="text-subtle">Policy Compliance</span>
        <span className={cn("font-mono-tabular font-semibold", tone)}>{(comp.overall * 100).toFixed(0)}%</span>
      </div>
      <div className="h-1 bg-surface-2 rounded-sm overflow-hidden"><div className={cn("h-full", bg)} style={{ width: `${comp.overall * 100}%` }} /></div>
    </div>
  );
}

function CellButton({ cell, onOpen }) {
  return (
    <button data-testid={`abcxyz-${cell.code}`} onClick={() => onOpen(cell)}
            className={cn("min-w-0 break-words border rounded-sm p-2 sm:p-2.5 text-left transition-[transform,border-color,box-shadow] duration-150 hover:border-primary hover:shadow-md hover:-translate-y-0.5 group",
              cell.code === "AX" ? "border-success/40 bg-success/5" : cell.code === "CZ" ? "border-critical/40 bg-critical/5" : "border-border bg-surface-2/40")}>
      <div className="flex flex-wrap items-center justify-between gap-x-1 mb-1">
        <span className="font-mono-tabular font-semibold">{cell.code}</span>
        <span className="text-[10px] text-muted inline-flex items-center gap-1" data-testid={`abcxyz-count-${cell.code}`}>
          {formatNumber(cell.count)} SKUs <ChevronRight size={10} className="opacity-0 group-hover:opacity-100 text-primary" />
        </span>
      </div>
      <div className="font-mono-tabular text-xs font-semibold" data-testid={`abcxyz-value-${cell.code}`}>
        {formatPeso(cell.value, { compact: true })} <span className="text-[9.5px] text-subtle font-normal">· {(cell.share * 100).toFixed(1)}%</span>
      </div>
      <div className="text-[10px] text-muted leading-snug mt-1 mb-1 min-h-[26px]">{cell.policy}</div>
      {cell.weather_in > 0 && (
        <div className="text-[9.5px] text-success inline-flex items-center gap-1" title="SKUs moved into this cell by the weather rule">
          <CloudSun size={9} /> {formatNumber(cell.weather_in)} weather-predictable
        </div>
      )}
      <Meter code={cell.code} />
    </button>
  );
}

function IntermittentBand({ cell, settings, onOpen }) {
  const s = cell.abc_split;
  return (
    <button data-testid="abcxyz-ZI" onClick={() => onOpen(cell)}
            className="mt-1 w-full border border-dashed border-border-strong bg-surface-2/30 rounded-sm px-3 py-2.5 text-left flex flex-wrap items-center gap-x-5 gap-y-1 hover:border-primary hover:-translate-y-0.5 transition-[transform,border-color] duration-150 group">
      <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest">
        <Hourglass size={12} className="text-muted" /> Z-Intermittent / Slow-Moving
      </span>
      <span className="font-mono-tabular text-[12px] font-semibold" data-testid="abcxyz-count-ZI">{formatNumber(cell.count)} SKUs</span>
      <span className="font-mono-tabular text-[11.5px]">{formatPeso(cell.value, { compact: true })}</span>
      <span className="text-[10.5px] text-muted">Fewer than {settings.slow_mover_units} units in 12 months · skip the variability formula</span>
      <span className="text-[10px] text-subtle font-mono-tabular ml-auto">A {s.A} · B {s.B} · C {formatNumber(s.C)}</span>
      <ChevronRight size={12} className="text-primary opacity-0 group-hover:opacity-100" />
    </button>
  );
}

// Pipeline-backed ABC × XYZ matrix with the Z-Intermittent band.
export default function AbcXyzMatrix({ summary, error, onOpenCell, className }) {
  const branch = summary?.branch;
  const byCode = summary ? Object.fromEntries(summary.cells.map(c => [c.code, c])) : {};
  const s = summary?.settings;
  return (
    <Tile className={className} testid="abc-xyz-matrix" origin={summary?.origin}
          title="ABC × XYZ Classification — Value × Variability"
          action={summary && <div className="text-[10px] uppercase tracking-widest text-muted" data-testid="abcxyz-totals">
            {branch ? `${branch.name} · ` : "Network · "}{formatNumber(summary.stats.skus_classified)} SKUs · {formatPeso(summary.stats.total_value, { compact: true })}
          </div>}>
      {!summary && !error && <div className="py-16 text-center text-muted text-[12px]"><Loader2 size={14} className="inline animate-spin mr-2" />Running classification pipeline…</div>}
      {error && <div data-testid="abcxyz-error" className="text-[12px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-3">{error}</div>}
      {summary && (
        <>
          {branch && (
            <div data-testid="abcxyz-branch-note" className="mb-3 text-[11px] text-info border border-info/30 bg-info/5 rounded-sm px-3 py-1.5 inline-flex flex-wrap items-center gap-1.5">
              <MapPin size={11} /> <strong>{branch.name} branch</strong> — the network's rules applied to this branch's own sales ·
              {" "}{formatNumber(branch.carried)} of {formatNumber(branch.network_skus)} SKUs sold or stocked here
            </div>
          )}
          <div className="grid grid-cols-[40px_repeat(3,minmax(0,1fr))] sm:grid-cols-[72px_repeat(3,minmax(0,1fr))] gap-1 text-[11px]">
            <div />
            {[["X", `CV ≤ ${s.x_max}`, "Stable"], ["Y", `CV ≤ ${s.y_max}`, "Variable"], ["Z", `CV > ${s.y_max}`, "Volatile"]].map(([k, cv, l]) => (
              <div key={k} className="min-w-0 text-center text-muted uppercase tracking-wider sm:tracking-widest font-semibold pb-1 text-[10px] sm:text-[11px]">
                {k} — {l}<div className="text-[9px] text-subtle normal-case tracking-normal font-mono-tabular font-normal">{cv}</div>
              </div>
            ))}
            {["A", "B", "C"].map(row => (
              <React.Fragment key={row}>
                <div className="flex flex-col items-end justify-center pr-2 text-muted uppercase tracking-widest font-semibold">
                  {row}<span className="hidden sm:block text-[8.5px] text-subtle normal-case tracking-normal font-normal text-right leading-tight">{ROW_LABEL[row]}</span>
                </div>
                {["X", "Y", "Z"].map(col => <CellButton key={col} cell={byCode[`${row}${col}`]} onOpen={onOpenCell} />)}
              </React.Fragment>
            ))}
          </div>
          <IntermittentBand cell={byCode.ZI} settings={s} onOpen={onOpenCell} />
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px] text-muted">
            <span><strong className="text-fg">Pipeline:</strong> {formatNumber(summary.stats.legacy_merged)} legacy parts merged · {formatNumber(summary.stats.skus_capped)} SKUs with capped outliers · {formatNumber(summary.stats.weather_reclassified)} weather-reclassified</span>
            <span className="inline-flex items-center gap-1.5 ml-auto">Compliance meters are sample figures <OriginBadge {...syn("ABC × XYZ policy compliance")} testid="abcxyz-compliance-origin" /></span>
          </div>
        </>
      )}
    </Tile>
  );
}
